<template>
	<div class="graph-nd graph-2d">
		<svg :viewBox="'0 0 ' + width + ' ' + height" xmlns="http://www.w3.org/2000/svg">
			<Legend v-bind:width="width"
				v-bind:height="height"
				v-bind:startDate="startDate"
				v-bind:bins="bins.length"
				v-bind:binWidth="binWidth">
			</Legend>

			<g v-for="(bin, index) in bins" :key="index"
				class="tooltip-trigger"
				@click="showTooltip(bin, ...arguments)"
				@mouseover="showTooltip(bin, ...arguments)"
				@mouseout="hideTooltip">

				<template v-if="bin.length == 1">
					<text :x="centerScaleX(index)"
						  :y="scaleY(firstValue(bin)) - style.topLabelOffset"
						  :style="style.labelFont"
						  text-anchor="middle">
						  {{ withPrecision(firstValue(bin)) }}
					</text>
					<circle :cx="centerScaleX(index)"
							:cy="scaleY(firstValue(bin))"
							:r="style.pointRadius"
							:fill="style.pointBackgroundColor">
					</circle>
					<circle :cx="centerScaleX(index)"
							:cy="scaleY(firstValue(bin))"
							:r="style.pointRadius / 2"
							:fill="style.pointColor">
					</circle>
				</template>

				<template v-else-if="bin.length > 1">

					<template v-if="Math.abs(scaleY(firstValue(bin)) - scaleY(lastValue(bin))) < style.pointRadius * 2">
						<text :x="centerScaleX(index)"
							  :y="scaleY(firstValue(bin)) - style.topLabelOffset"
							  :style="style.labelFont"
							  text-anchor="middle">
							  {{ withPrecision(firstValue(bin)) }}
						</text>
						<circle :cx="centerScaleX(index)"
								:cy="scaleY(firstValue(bin))"
								:r="style.pointRadius"
								:fill="style.pointBackgroundColor">
						</circle>
						<circle :cx="centerScaleX(index)"
								:cy="scaleY(firstValue(bin))"
								:r="style.pointRadius / 2"
								:fill="style.pointColor">
						</circle>
						<text v-if="withPrecision(lastValue(bin)) != withPrecision(firstValue(bin))"
						      :x="centerScaleX(index)"
							  :y="scaleY(firstValue(bin)) + 25"
							  :style="style.labelFont"
							  text-anchor="middle">
							  {{ withPrecision(lastValue(bin)) }}
						</text>
					</template>
					<template v-else>

						<text :x="centerScaleX(index)"
							  :y="scaleY(firstValue(bin)) - style.topLabelOffset + style.labelFont.fontSize"
							  :style="style.labelFont"
							  text-anchor="middle">
							  {{ withPrecision(firstValue(bin)) }}
						</text>
						<rect :x="centerScaleX(index) - style.pointRadius"
							  :y="scaleY(firstValue(bin))"
							  :rx="style.pointRadius"
							  :width="style.pointRadius * 2"
							  :height="Math.abs(scaleY(firstValue(bin)) - scaleY(lastValue(bin)))"
							  :fill="style.pointBackgroundColor">
						</rect>
						<rect :x="centerScaleX(index) - style.pointRadius / 2"
							  :y="scaleY(firstValue(bin)) + style.pointRadius / 2"
							  :rx="style.pointRadius / 2"
							  :width="style.pointRadius"
							  :height="Math.max(Math.abs(scaleY(firstValue(bin)) - scaleY(lastValue(bin))) - style.pointRadius, style.pointRadius)"
							  :fill="style.pointColor">
						</rect>
						<text v-if="withPrecision(lastValue(bin)) != withPrecision(firstValue(bin))"
							  :x="centerScaleX(index)"
							  :y="scaleY(lastValue(bin)) + style.bottomLabelOffset"
							  :style="style.labelFont"
							  text-anchor="middle">
							  {{ withPrecision(lastValue(bin)) }}
						</text>

					</template>

				</template>
			</g>
		</svg>

		<Tooltip v-bind:display="tooltipDisplay" @hover="hoverTooltip">
			<p v-for="(point, i) in tooltipBin" :key="i">
				<strong>{{ withPrecision(point.value) }} {{ unit || '' }}</strong><br>
				<small>{{ formatDateTime(point.time) }}</small>
			</p>
		</Tooltip>
	</div>
</template>

<script>
import Graph from '.'
import style from './config/style'

const oneDay = 86400000

export default {
	name: 'graph-1d',
	extends: Graph,

	props: {
		precision: {
			type: Number,
			default: 0
		}
	},

	computed: {
		/**
		 * @function 	bins
		 * @desc 		Bins the data points into day-wide bins for the interval determined by the current scale
		 */
		bins() {
			var bins = []

			for (var t = this.startDate.getTime() ; t <= this.date.getTime() ; t += this.binInterval) {
				bins.push(this.data.filter((point) =>
					(t <= point.time && point.time <= t + this.binInterval)
				).sort((a, b) => {
					return b.value - a.value
				}))
			}

			return bins
		},

		/**
		 * @function 	maxValue
		 * @desc 		Maximum value that a point can take within date range
		 */
		maxValue() {
			var s = this.startDate.getTime(),
				e = this.date.getTime()

			return Math.max.apply(null, this.data
				.filter((point) => (s <= point.time && point.time <= e))
				.map((point) => point.value))
		},

		/**
		 * @function 	minValue
		 * @desc 		Minimum value that a point can take within date range
		 */
		minValue() {
			var s = this.startDate.getTime(),
				e = this.date.getTime()

			return Math.min.apply(null, this.data
				.filter((point) => (s <= point.time && point.time <= e))
				.map((point) => point.value))
		},

		range() {
			return Math.max(this.maxValue - this.minValue, 0)
		}
	},
	methods: {
		setScale(scale) {
			this.scale = scale
		},

		/**
		 * @function 	withPrecision
		 * @desc 		rounding the given value w.r.t the precision
		 * @param 		{value} Number
		 */
		withPrecision(value) {
			if (this.precision == 0 || Math.abs(value - Math.round(value)) < 0.01) {
				return Math.round(value)
			}
			else {
				return value.toFixed(this.precision)
			}
		},
		/**
		 * @function 	firstValue
		 * @desc 		Return the first value in the bin.
		 */
		firstValue(bin) {
			return bin[0].value
		},
		/**
		 * @function 	lastValue
		 * @desc 		Return the last value in the bin.
		 */
		lastValue(bin) {
			return bin[bin.length - 1].value
		},
		scaleX(x) {
			return x * this.width / this.bins.length
		},
		scaleY(y) {
			if (this.range == 0)
				return (this.height - 35) / 2
			return this.legendStart - ((y - this.minValue) / this.range) * this.displayHeight - 25
		},
		centerScaleX(x) {
			return (x + 0.5) * this.width / (this.bins.length + 1)
		}
  	}
}
</script>

<style lang="scss" scoped>
</style>
